import React, { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import {
  AccountManager,
  btag,
  constants,
  pushToDataLayer,
  SessionStorageManager,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useDetection,
} from '@atlas/core';
import { onSignUpStepSubmit, onSignUpSubmit, useActiveForm } from '@atlas/react-forms';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';
import { BasePopup, Label } from '@atlas/ui';

import { usePostHog, useRegistrationPostHog } from 'hooks/usePostHog';
import {
  ACCOUNT_STATUSES,
  BTAG_STORAGE_KEY,
  CXFROM_STORAGE_KEY,
  OAUTH_FLOW_TYPES,
  POPUP_SIGN_UP_DELAY_STORAGE_KEYS,
} from 'src/common/constants';
import { useSignupPopup } from 'src/components/SignUp/useSignUpPopup';
import useSocialProviders from 'src/components/SignUp/useSocialProviders';

import useAutoAdvancePrefilledSteps from './hooks/useAutoAdvancePrefilledSteps';
import useOAuthRegistrationForm from './hooks/useOAuthRegistrationForm';
import useTouchPrefilledFields from './hooks/useTouchPrefilledFields';
import { getMultistepConfig } from './getMultistepConfig';
import { GTM_TRACKED_FIELDS, gtmFieldChangesHandler } from './SignUp';
import SignUpContent from './SignUpContent';
import SignUpFormBody from './SignUpFormBody';
import SignUpLayout from './SignUpLayout';
import useLeaveSignupBlocker from './useLeaveSignupBlocker';
import useRegistrationFlowActive from './useRegistrationFlowActive';
import useRegistrationStepAnalytics from './useRegistrationStepAnalytics';
import useSetMultiStepForPartiallyRegistered from './useSetMultiStepForPartiallyRegistered';
import useSignUpAction from './useSignUpAction';
import useStepFields from './useStepFields';

const { payment } = clientProfileRoutes;
const { deposit } = paymentRoutes;
const { CLIENT_PROFILE } = COMPONENT_TYPES;

const SingleSignUp = ({ optimized = false }) => {
  const bypassLeaveBlockRef = useRef(false);
  const formRef = useRef(null);
  const history = useHistory();
  const { browser, ip, locale, os } = useDetection();
  const { account, authenticated, resolved } = useApplicationState();

  const params = new URLSearchParams(window.location.search);
  const cxFromParam = params.get('cxfrom');
  const cxFromPath = StorageManager.getItem(CXFROM_STORAGE_KEY);
  const leaveSignup = SiteConfigManager.getConfig(
    'config.config.general.leaveRegistrationPopup.enable',
    false,
  );
  const leaveAfterFirstStep = SiteConfigManager.getConfig(
    'config.config.registration.close_multistep_after_first_step',
    false,
  );
  const enableSignUpDelay = SiteConfigManager.getConfig('config.config.registration.mobile_popup');
  const isShownProgressBar = SiteConfigManager.getConfig(
    'config.config.registration.showRegistrationProgressBar',
    true,
  );

  const { closeSignupPopup } = useSignupPopup();

  let historyPush;
  try {
    const history = useHistory();
    historyPush = history?.push;
  } catch {
    historyPush = null;
  }

  const navigate = useCallback(
    url => {
      if (historyPush) {
        historyPush(url); // SPA navigation
      } else {
        window.location.href = url; // full reload
      }
    },
    [historyPush],
  );

  const postHog = usePostHog();

  const { getActiveFormsWithOAuth, isOAuthRegistrationRef } = useOAuthRegistrationForm();

  const {
    activeStep,
    activeType,
    resetActiveForm,
    setActiveStep,
    setActiveType,
    stepSubmit,
    stepsAmount,
    submit,
    types,
  } = useActiveForm(
    'CLIENT_REGISTRATION',
    onSignUpSubmit(formData => {
      pushToDataLayer('registration_form_button_click', btag);
      for (const field in formData) {
        if (formData[field] === null) {
          // eslint-disable-next-line no-param-reassign
          delete formData[field];
        }
      }
      // email hashing will be implement in FT-7781
      return AccountManager.formRegistration(formData).then(result => {
        handleRegistrationSuccess({ clientId: result.clientId, email: formData.EMAIL });
        return result;
      });
    }),
    getActiveFormsWithOAuth,
    onSignUpStepSubmit(formData => {
      return AccountManager.formRegistrationSubmitStep(formData);
    }),
  );

  useSetMultiStepForPartiallyRegistered({
    accountStatus: account.status,
    activeType,
    setActiveType,
    types,
  });
  const isMultiStepFlow = activeType?.templateName === 'MULTI_STEP';
  const isUserFullyRegistered =
    resolved && authenticated && account.status !== ACCOUNT_STATUSES.PARTIALLY_REGISTERED;

  useEffect(() => {
    pushToDataLayer('registration_start', { action: 'start' }, btag);
  }, []);

  const { handleBackClick, handleLeave, handleNextStep, handleRegistrationSuccess } =
    useRegistrationPostHog({
      activeStep,
      cxFromParam,
      cxFromPath,
      postHog,
      stepsAmount,
    });

  const signUpPopup = optimized
    ? false
    : SiteConfigManager.getConfig('config.config.registration.showInPopup', false);
  useRegistrationFlowActive({ enabled: !isUserFullyRegistered && isMultiStepFlow });

  useEffect(() => {
    const nextDayMs = dayjs().add(1, 'd').valueOf();
    if (enableSignUpDelay && location.search.includes('cmd=signup')) {
      SessionStorageManager.setItem(POPUP_SIGN_UP_DELAY_STORAGE_KEYS.NEXT_SHOW, nextDayMs);
    }
  }, [enableSignUpDelay]);

  useLeaveSignupBlocker({
    enabled:
      leaveSignup &&
      !isUserFullyRegistered &&
      !optimized &&
      (signUpPopup || location.search !== '?cmd=signup'),
    history,
    isMultiStepFlow,
    isPartiallyRegistered: account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED,
    onLeave: handleLeave,
    popupType: 'signupPopup',
    shouldBypassBlock: () => bypassLeaveBlockRef.current,
  });

  const defaultPageAfterRegistration = SiteConfigManager.getConfig(
    'config.config.links.defaultPageAfterRegistration',
  );
  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);

  const isLastStep = stepsAmount && activeStep === activeType.steps.length - 1;
  const isFirstStep = activeStep === 0;
  const { headerTitle, showSignInLink } = getMultistepConfig(isMultiStepFlow, isFirstStep, account);

  useRegistrationStepAnalytics({
    activeStep,
    browser,
    device: constants?.DEVICE_TYPES?.DESKTOP,
    ip,
    locale,
    os,
    stepsAmount,
  });

  useTouchPrefilledFields({
    activeStep,
    activeType,
    formRef,
    isOAuthRegistration: isOAuthRegistrationRef.current,
  });

  useAutoAdvancePrefilledSteps({
    activeType,
    isOAuthRegistration: isOAuthRegistrationRef.current,
    setActiveStep,
  });

  const redirectAfterRegistration = useCallback(() => {
    const cxfrom = optimized ? cxFromParam || cxFromPath : '';
    const pageAfterRegistration =
      defaultPageAfterRegistration?.indexOf('//') > -1
        ? defaultPageAfterRegistration.split('/').reduce((acc, next, index) => {
            if (index < 3) return acc;
            return `${acc}/${next}`;
          }, '')
        : defaultPageAfterRegistration;

    const isMultiStepFirstStepExit = isMultiStepFlow && isFirstStep && leaveAfterFirstStep;

    const redirectPath = isMultiStepFirstStepExit
      ? '/'
      : cxfrom || pageAfterRegistration || `${clientProfilePath}/${payment}/${deposit}`;

    // allow post-submit internal redirect without triggering leave popup.
    bypassLeaveBlockRef.current = true;

    navigate(redirectPath);
    StorageManager.removeItem(CXFROM_STORAGE_KEY);
    StorageManager.removeItem(BTAG_STORAGE_KEY);
  }, [
    optimized,
    cxFromParam,
    cxFromPath,
    defaultPageAfterRegistration,
    navigate,
    clientProfilePath,
    isMultiStepFlow,
    isFirstStep,
    leaveAfterFirstStep,
  ]);

  const action = useSignUpAction({
    activeStep,
    isFirstStep,
    isLastStep,
    isMultiStepFlow,
    leaveAfterFirstStep,
    onNextStep: handleNextStep,
    redirectAfterRegistration,
    resetActiveForm,
    setActiveStep,
    stepSubmit,
    submit,
  });

  const isBackButtonVisible = activeStep > (isMultiStepFlow ? 1 : 0);

  const onBackHandler = useCallback(() => {
    handleBackClick();
    setActiveStep(activeStep - 1);
  }, [setActiveStep, activeStep, handleBackClick]);

  const fields = useStepFields({ form: activeType, stepNumber: activeStep });

  const socialProviders = useSocialProviders(activeType);
  const flowType = isMultiStepFlow ? OAUTH_FLOW_TYPES.SIGNUP_MULTISTEP : OAUTH_FLOW_TYPES.SIGNUP;

  const shouldShowStepper = !isMultiStepFlow || isShownProgressBar;

  const renderForm = activeType && (
    <SignUpFormBody
      action={action}
      activeStep={activeStep}
      activeType={activeType}
      fields={fields}
      flowType={flowType}
      formRef={formRef}
      hideSocialProviders={isOAuthRegistrationRef.current}
      isBackButtonVisible={isBackButtonVisible}
      isLastStep={isLastStep}
      onBackHandler={onBackHandler}
      onTrackedFieldChanges={gtmFieldChangesHandler}
      shouldShowStepper={shouldShowStepper}
      showSignInLink={showSignInLink}
      signInLink={
        <a href="?cmd=signin">
          <Label className="title-bold-text link" message="Sign in" />
        </a>
      }
      socialProviders={socialProviders}
      stepsAmount={stepsAmount}
      trackedFields={GTM_TRACKED_FIELDS}
    />
  );

  return signUpPopup ? (
    <BasePopup className="signup-popup">
      <SignUpLayout onClose={closeSignupPopup} signUpPopup={signUpPopup} title={headerTitle}>
        <SignUpContent
          activeType={activeType}
          renderForm={renderForm}
          setActiveType={setActiveType}
          types={types}
        />
      </SignUpLayout>
    </BasePopup>
  ) : (
    <div className="container-fluid">
      <div className="signup">
        <SignUpLayout onClose={closeSignupPopup} signUpPopup={signUpPopup} title={headerTitle}>
          <SignUpContent
            activeType={activeType}
            renderForm={renderForm}
            setActiveType={setActiveType}
            types={types}
          />
        </SignUpLayout>
      </div>
    </div>
  );
};

SingleSignUp.propTypes = {
  optimized: PropTypes.bool,
};

export default SingleSignUp;
